import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { errorMessage } from './client';
import type { Page } from './types';

/**
 * Размер страницы во всех списках со «Показать ещё».
 *
 * Больше ста сервер не отдаёт: у каждого списка в DTO стоит Max(100), и
 * запрос с limit=150 отбивается кодом 400. Поэтому кнопка наращивает не
 * limit, а смещение: limit остаётся постоянным, страницы копятся в списке.
 */
export const PAGE_SIZE = 50;

/**
 * Сколько сочетаний фильтров помнит каждый список, давние вытесняются первыми.
 * Предел у каждого свой, а не общий: один реестр даёт шестнадцать сочетаний
 * вкладки и общежития, не считая поисков, и общий предел вытеснял бы ими
 * очереди и внешки.
 */
const KEPT_FILTERS = 4;

interface Shown<T> {
  key: string;
  items: T[];
  total: number;
}

/** Имя списка → ключ фильтров → прочитанное; порядок вставки и есть давность. */
const keptLists = new Map<string, Map<string, Page<unknown>>>();

function readKept<T>(keepAs: string | undefined, key: string): Shown<T> | null {
  const saved = keepAs === undefined ? undefined : keptLists.get(keepAs)?.get(key);
  if (saved === undefined) return null;
  return { key, items: saved.items as T[], total: saved.total };
}

function pagesOf(list: Shown<unknown> | null): number {
  return list === null ? 1 : Math.max(1, Math.ceil(list.items.length / PAGE_SIZE));
}

function keep(keepAs: string, key: string, page: Page<unknown>): void {
  const filters = keptLists.get(keepAs) ?? new Map<string, Page<unknown>>();
  keptLists.set(keepAs, filters);
  filters.delete(key);
  filters.set(key, page);
  if (filters.size <= KEPT_FILTERS) return;
  const oldest = filters.keys().next().value;
  if (oldest !== undefined) filters.delete(oldest);
}

/** Вынуть строки из памяти списка — из всех его фильтров сразу. */
export function dropKeptRows<T>(keepAs: string, drop: (row: T) => boolean): void {
  const filters = keptLists.get(keepAs);
  if (filters === undefined) return;
  for (const [key, page] of filters) {
    const items = (page.items as T[]).filter((row) => !drop(row));
    filters.set(key, { items, total: page.total - (page.items.length - items.length) });
  }
}

/** Строки читались под прежней ролью: перезапуск сессии их забывает. */
export function forgetKeptLists(): void {
  keptLists.clear();
}

/** Что запросить у сервера: постраничность везде одна — размер и смещение. */
export interface PageParams {
  limit: number;
  offset: number;
}

export interface PagedLoadable<T> {
  /** Всё прочитанное одним списком; null — не прочитано ещё ничего. */
  data: Page<T> | null;
  /** Строки на экране — от прошлых фильтров: новые ещё не ответили. */
  leftover: boolean;
  loading: boolean;
  error: string | null;
  /** Перечитать показанное с сервера, не теряя дочитанных страниц. */
  reload: () => void;
  /** Дочитать следующую страницу. null — читать больше нечего или чтение ещё идёт. */
  loadMore: (() => void) | null;
}

/** Где мы в списке: ключ фильтров, сколько страниц показано, откуда читать. */
interface Cursor {
  key: string;
  pages: number;
  /** С какой страницы читаем: 0 — заново всё, pages-1 — только хвост. */
  from: number;
  /** Счётчик перечитываний: он и делает повтор того же чтения новым состоянием. */
  attempt: number;
}

/**
 * Постраничная загрузка с накоплением: тот же useLoader, но список не
 * перезапрашивается целиком одним растущим limit, а дочитывается страницами.
 *
 * enabled и отмена работают как в useLoader — смысл флага описан там же.
 * Смена фильтров начинает список заново, reload перечитывает ровно столько
 * страниц, сколько уже показано: иначе после правки строка со второй страницы
 * пропала бы с экрана вместе с открытой над ней карточкой.
 *
 * `keepAs` — имя списка в памяти возврата: экран, на который возвращаются,
 * рисует прочитанные страницы в первом же рендере — VKUI восстанавливает
 * прокрутку именно тогда — и перечитывает их в фоне.
 */
export function usePagedLoader<T>(
  load: (params: PageParams, signal: AbortSignal) => Promise<Page<T>>,
  deps: unknown[],
  enabled = true,
  keepAs?: string,
): PagedLoadable<T> {
  const key = JSON.stringify(deps);

  // Ключ фильтров при списке: после неудачной смены фильтра на экране ещё
  // прежние строки, и дописывать к ним страницу нового фильтра нельзя.
  const [list, setList] = useState<Shown<T> | null>(() => readKept<T>(keepAs, key));
  const [settled, setSettled] = useState<{ cursor: Cursor; error: string | null } | null>(null);
  const [cursor, setCursor] = useState<Cursor>(() => ({
    key,
    pages: pagesOf(list),
    from: 0,
    attempt: 0,
  }));

  // Сброс на первую страницу считаем прямо в рендере, а не эффектом: иначе
  // при смене фильтра сначала ушёл бы запрос со старым смещением.
  if (cursor.key !== key) {
    const saved = readKept<T>(keepAs, key);
    setCursor({ key, pages: pagesOf(saved), from: 0, attempt: 0 });
    if (saved !== null) setList(saved);
  }

  // Функция пересоздаётся на каждом рендере — в зависимости её класть нельзя.
  const loadRef = useRef(load);
  useLayoutEffect(() => {
    loadRef.current = load;
  });

  useEffect(() => {
    if (!enabled) return undefined;
    const controller = new AbortController();

    void (async () => {
      try {
        const collected: T[] = [];
        let last: Page<T> | null = null;
        for (let index = cursor.from; index < cursor.pages; index += 1) {
          last = await loadRef.current(
            { limit: PAGE_SIZE, offset: index * PAGE_SIZE },
            controller.signal,
          );
          collected.push(...last.items);
        }
        if (controller.signal.aborted) return;
        // Хвост дописываем к уже показанному, полное чтение — заменяет список.
        // Срез, а не конкатенация: повторный проход эффекта не должен
        // задваивать строки.
        setList((prev) => ({
          key: cursor.key,
          items:
            cursor.from > 0
              ? [...(prev?.items ?? []).slice(0, cursor.from * PAGE_SIZE), ...collected]
              : collected,
          total: last?.total ?? 0,
        }));
        setSettled({ cursor, error: null });
      } catch (cause: unknown) {
        if (controller.signal.aborted) return;
        setSettled({ cursor, error: errorMessage(cause) });
      }
    })();

    return () => controller.abort();
  }, [enabled, cursor]);

  // Пишем эффектом: рендер модульную память не трогает.
  useEffect(() => {
    if (keepAs !== undefined && list !== null) {
      keep(keepAs, list.key, { items: list.items, total: list.total });
    }
  }, [keepAs, list]);

  const reload = useCallback(
    () => setCursor((prev) => ({ ...prev, from: 0, attempt: prev.attempt + 1 })),
    [],
  );

  const current = settled?.cursor === cursor ? settled : null;
  const shown = list !== null && list.key === cursor.key ? list : null;

  // Следующая страница — от того, что реально показано, а не от счётчика
  // курсора: после неудачного дочитывания он уже сдвинут, и страница
  // пропала бы. Смещения кратны странице, поэтому неполный хвост читается
  // заново. prev === cursor отсекает повторное нажатие до перерисовки.
  const next = shown === null ? 0 : Math.floor(shown.items.length / PAGE_SIZE);
  const readMore = useCallback(
    () => setCursor((prev) => (prev === cursor ? { ...prev, pages: next + 1, from: next } : prev)),
    [cursor, next],
  );

  const hasMore = current !== null && shown !== null && shown.items.length < shown.total;

  return {
    data: list === null ? null : { items: list.items, total: list.total },
    leftover: list !== null && shown === null,
    loading: enabled && current === null,
    error: current?.error ?? null,
    reload,
    loadMore: hasMore ? readMore : null,
  };
}
